import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

type Slot = {
  originalFile: File | null
  originalUrl: string | null
  croppedBlob: Blob | null
  croppedUrl: string | null
  mime: string | null
}

type State = {
  slots: Record<string, Slot>
  setOriginal: (id: string, file: File) => void
  setCropped: (id: string, blob: Blob, mime?: string) => void
  clear: (id: string) => void
  clearAll: () => void
}

const emptySlot: Slot = {
  originalFile: null,
  originalUrl: null,
  croppedBlob: null,
  croppedUrl: null,
  mime: null
}

export const useImageSlots = create<State>()(
  devtools((set, get) => ({
    slots: {},

    setOriginal: (id, file) =>
      set(state => {
        const prev = state.slots[id]
        if (prev?.originalUrl) URL.revokeObjectURL(prev.originalUrl)
        if (prev?.croppedUrl) URL.revokeObjectURL(prev.croppedUrl)
        return {
          slots: {
            ...state.slots,
            [id]: {
              originalFile: file,
              originalUrl: URL.createObjectURL(file),
              croppedBlob: null,
              croppedUrl: null,
              mime: null
            }
          }
        }
      }),

    setCropped: (id, blob, mime) =>
      set(state => {
        const prev = state.slots[id] ?? emptySlot
        if (prev.croppedUrl) URL.revokeObjectURL(prev.croppedUrl)
        return {
          slots: {
            ...state.slots,
            [id]: {
              ...prev,
              croppedBlob: blob,
              croppedUrl: URL.createObjectURL(blob),
              mime: mime ?? blob.type ?? null
            }
          }
        }
      }),

    clear: id =>
      set(state => {
        const prev = state.slots[id]
        if (prev?.originalUrl) URL.revokeObjectURL(prev.originalUrl)
        if (prev?.croppedUrl) URL.revokeObjectURL(prev.croppedUrl)
        const next = { ...state.slots }
        delete next[id]
        return { slots: next }
      }),

    clearAll: () => {
      const { slots } = get()
      Object.values(slots).forEach(s => {
        if (s.originalUrl) URL.revokeObjectURL(s.originalUrl)
        if (s.croppedUrl) URL.revokeObjectURL(s.croppedUrl)
      })
      set({ slots: {} })
    }
  }))
)
